import axios, { AxiosError, AxiosResponse } from "axios";

import Utils, { ServerModelWithDateStrings } from "@teamprevent/common/utils";
import { LinkObject } from "@teamprevent/common/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { PhysiotherapyReferralCaseCreateModel } from "@teamprevent/common/physiotherapy/models";
import { toast } from "@teamprevent/common/toasts";

import { TaskType, parseTaskModel, OhCaseSearchDetails, AddFileNoteModel } from "../../task/model";
import { HealthSurveillanceCaseCreateModel, ReopenHealthSurveillanceCaseModel } from "../../healthSurveillance/models";
import { EppDecision, Covid19VaccinationStatus } from "../../immunisations/model";
import { AddUnknownEmployeeModel } from "../../calendar/clinics/models";

import {
    OhCaseListModel,
    OhCaseDetailModel,
    OhCaseCreateModel,
    ImmunisationCaseCreateModel,
    parseOhCaseModel,
    CreateImmunisationCaseResult,
    CreatePhysiotherapyReferralCaseResult,
    OhCaseDocument,
    parseOhCaseModelWithDocuments
} from "./model";

class OhCaseApi {
    private readonly apiUrl = "/api/case";

    public async getAllForEmployeeRecord(customerId: string, employeeRecordId: string): Promise<OhCaseListModel[]> {
        const apiUrl = `${this.apiUrl}/${customerId}/employeerecordid/${employeeRecordId}${Utils.getOnlyParameters()}`;
        const response = await axios.get<ServerModelWithDateStrings<OhCaseListModel>[]>(apiUrl);
        return response.data.map(parseOhCaseModel);
    }

    public async getLastTaskAssignedUser(taskType: TaskType, ohCaseId: string): Promise<string> {
        const apiUrl = `${this.apiUrl}/${ohCaseId}/lastAssignedUser?taskType=${taskType?.type}`;
        const response = await axios.get(apiUrl);
        return response.data as string;
    }

    public async getAllForEmployeeRecordWithDetails(
        customerId: string, employeeRecordId: string
    ): Promise<(OhCaseDetailModel & { documents: OhCaseDocument[] })[]> {
        const apiUrl = `${this.apiUrl}/${customerId}/employeerecordid/${employeeRecordId}`;
        const response = await axios.get<
            undefined,
            AxiosResponse<ServerModelWithDateStrings<(OhCaseDetailModel & { documents: OhCaseDocument[] })>[]>
                >(apiUrl, { params: { includeDetails: true } });
        return response.data.map(parseOhCaseModelWithDocuments);
    }

    public async detail(customerId: string, ohCaseId: string): Promise<OhCaseDetailModel> {
        const query = "?parameters=" + Utils.getParameters();
        const response = await axios.get<ServerModelWithDateStrings<OhCaseDetailModel>>(`${this.apiUrl}/${customerId}/${ohCaseId}${query}`);
        return parseOhCaseModel(response.data);
    }

    public async updateAssociatedManagers(customerId: string, ohCaseId: string, associatedManagers: LinkObject[]): Promise<void> {
        await axios.post(`${this.apiUrl}/${customerId}/${ohCaseId}/associatedManagers`, associatedManagers);
    }

    public async updateAssociatedHrUsers(customerId: string, ohCaseId: string, associatedHrUsers: LinkObject[]): Promise<void> {
        await axios.post(`${this.apiUrl}/${customerId}/${ohCaseId}/associatedHrUsers`, associatedHrUsers);
    }

    public async updatePurchaseOrderNumber(customerId: string, ohCaseId: string, purchaseOrderNumber: string): Promise<void> {
        const data = { PurchaseOrderNumber: purchaseOrderNumber };
        await axios.post(`${this.apiUrl}/${customerId}/${ohCaseId}/financeInformation`, data);
    }

    public async setCaseConfidentiality(customerId: string, ohCaseId: string, isCaseTotallyConfidential: boolean, reason: string) {
        try {
            const url = isCaseTotallyConfidential
                ? `${this.apiUrl}/${customerId}/${ohCaseId}/confidentiality/confidential`
                : `${this.apiUrl}/${customerId}/${ohCaseId}/confidentiality/nonconfidential`;
            const response = await axios.post<ServerModelWithDateStrings<TaskModel[]>>(url, { reason });
            return response.data.map(parseTaskModel) ?? [];
        } catch (e) {
            const axiosError = e as AxiosError<string>;
            if (axiosError?.response?.status === 400) {
                throw new Error(axiosError?.response?.data);
            }

            throw e;
        }
    }

    public async create(ohCase: OhCaseCreateModel): Promise<OhCaseDetailModel> {
        const query = "?parameters=" + Utils.getParameters();
        const response = await axios.post<ServerModelWithDateStrings<OhCaseDetailModel>>(`${this.apiUrl}${query}`, ohCase);
        return parseOhCaseModel(response.data);
    }

    public async createPhysiotherapyReferralCase(
        physiotherapyReferralCase: PhysiotherapyReferralCaseCreateModel, apiUrl: string): Promise<CreatePhysiotherapyReferralCaseResult> {
        const response = await axios.post(apiUrl, physiotherapyReferralCase);
        return {
            physiotherapyReferralCase: parseOhCaseModel(response.data.physiotherapyReferralCase),
            linkedCase: response.data.linkedCase ? parseOhCaseModel(response.data.linkedCase) : null
        };
    }

    public async createImmunisationCase(
        customerId: string, employeeId: string, immunisationCase: ImmunisationCaseCreateModel): Promise<CreateImmunisationCaseResult> {
        try {
            const response = await axios.post(
                `/api/customer/${customerId}/employeeRecord/${employeeId}/immunisationCase`,
                { ...immunisationCase, dueDate: immunisationCase.dueDate.format("YYYY-MM-DD") }
            );
            return {
                immunisationCase: parseOhCaseModel(response.data.immunisationCase),
                linkedCase: response.data.linkedCase ? parseOhCaseModel(response.data.linkedCase) : null
            };
        } catch (e) {
            const axiosError = e as AxiosError<string>;
            if (axiosError?.response?.status === 400) {
                throw new Error(axiosError?.response?.data);
            }

            throw e;
        }
    }

    public async createBbvCase(customerId: string, employeeRecordId: string): Promise<OhCaseDetailModel> {
        const response = await axios.post<ServerModelWithDateStrings<OhCaseDetailModel>>(
            `/api/customer/${customerId}/employeeRecord/${employeeRecordId}/bbvCase`);

        return parseOhCaseModel(response.data);
    }

    public createHealthSurveillanceCase =
        async (customerId: string, employeeId: string, healthSurveillanceCase: HealthSurveillanceCaseCreateModel): Promise<OhCaseDetailModel> => {
            try {
                const response = await axios.post(`/api/customer/${customerId}/employeeRecord/${employeeId}/healthSurveillanceCase`, healthSurveillanceCase);
                return parseOhCaseModel(response.data);
            } catch (e) {
                const axiosError = e as AxiosError<string>;
                if (axiosError?.response?.status === 400) {
                    throw new Error(axiosError?.response?.data);
                }

                throw e;
            }
        };

    public createLinkedHealthSurveillanceCase =
        async (customerId: string, employeeId: string, healthSurveillanceCase: HealthSurveillanceCaseCreateModel): Promise<OhCaseDetailModel> => {
            try {
                const response = await axios.post(
                    `/api/customer/${customerId}/employeeRecord/${employeeId}/healthSurveillanceCase/linkedCase`,
                    healthSurveillanceCase);
                return parseOhCaseModel(response.data);
            } catch (e) {
                const axiosError = e as AxiosError<string>;
                if (axiosError?.response?.status === 400) {
                    throw new Error(axiosError?.response?.data);
                }

                throw e;
            }
        };

    public reopenHealthSurveillanceCase = async (customerId: string, employeeId: string, ohCaseId: string, model: ReopenHealthSurveillanceCaseModel) => {
        try {
            const response = await axios.post(
                `/api/customer/${customerId}/employeeRecord/${employeeId}/healthSurveillanceCase/${ohCaseId}/reopen`, model);
            return parseOhCaseModel(response.data);
        } catch (ex) {
            const axiosError = ex as AxiosError<string>;
            if (axiosError?.response?.status === 400) {
                throw new Error(axiosError?.response?.data);
            }

            throw ex;
        }
    };

    public async addFileNote(customerId: string, ohCaseId: string, model: AddFileNoteModel): Promise<TaskModel> {
        const response = await axios.post(`${this.apiUrl}/${customerId}/${ohCaseId}/addFileNote`, model);
        return parseTaskModel(response.data);
    }

    public async markAsImmsClear(
        customerId: string,
        ohCaseId: string,
        eppDecision: EppDecision,
        eppInternalNotes: string,
        eppNotes: string,
        covid19VaccinationStatus: Covid19VaccinationStatus,
        bookedDateOfFirstVaccination: moment.Moment,
        dateOfFirstVaccination: moment.Moment,
        dateOfSecondVaccination: moment.Moment) {
        try {
            return await axios.post(
                `${this.apiUrl}/${customerId}/${ohCaseId}/markAsImmsClear`,
                {
                    eppDecision,
                    internalNotes: eppInternalNotes,
                    notes: eppNotes,
                    covid19VaccinationStatus,
                    bookedDateOfFirstVaccination,
                    dateOfFirstVaccination,
                    dateOfSecondVaccination
                });
        } catch (e) {
            const axiosError = e as AxiosError<string>;
            if (axiosError?.response?.status === 400) {
                throw new Error(axiosError?.response?.data);
            }

            throw e;
        }
    }

    public async getPossibleOhCaseSearchDetails(caseId: number) {
        try {
            const response = await axios.get<OhCaseSearchDetails[]>(`${this.apiUrl}/search/${caseId}`);
            return response.data;
        } catch (error) {
            const axiosError = error as AxiosError;
            if (axiosError?.response?.status === 404) {
                toast.error("This case ID doesn't exist");
            }
            return [];
        }
    }

    public async matchOhCaseToExistingEmployee(
        customerId: string,
        ohCaseId: string,
        employeeRecordId: string
    ): Promise<OhCaseDetailModel & { employeeRecordId: string }> {
        const response = await axios.post<ServerModelWithDateStrings<OhCaseDetailModel & { employeeRecordId: string }>>(
            `${this.apiUrl}/${customerId}/${ohCaseId}/MatchToExistingEmployee/${employeeRecordId}`);

        return {
            ...parseOhCaseModel(response.data),
            employeeRecordId: response.data.employeeRecordId
        };
    }

    public async matchOhCaseToNewEmployee(
        customerId: string,
        ohCaseId: string,
        employeeModel: AddUnknownEmployeeModel
    ): Promise<OhCaseDetailModel & { employeeRecordId: string }> {
        const response = await axios.post<ServerModelWithDateStrings<OhCaseDetailModel & { employeeRecordId: string }>>(
            `${this.apiUrl}/${customerId}/${ohCaseId}/MatchToNewEmployee`, employeeModel);

        return {
            ...parseOhCaseModel(response.data),
            employeeRecordId: response.data.employeeRecordId
        };
    }
}

export const ohCaseApi = new OhCaseApi();
